import { del, get, patch, post } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { omit } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest";

import {
  UsersCreateSchema,
  UsersGetSchema,
  UsersUpdateSchema,
} from "@:openapi";

export interface Users {
  GET_ONE: {
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<UsersGetSchema>;
  };
  GET_ALL: {
    Response: CamelCasedPropertiesDeep<Array<UsersGetSchema>>;
  };
  GET_ME: {
    Response: CamelCasedPropertiesDeep<UsersGetSchema>;
  };
  PATCH: {
    RequestBody: CamelCasedPropertiesDeep<UsersUpdateSchema>;
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<UsersGetSchema>;
  };
  POST: {
    RequestBody: CamelCasedPropertiesDeep<UsersCreateSchema>;
    Response: CamelCasedPropertiesDeep<UsersGetSchema>;
  };
  DELETE: {
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<UsersGetSchema>;
  };
}

export const usersQueryKey = createQueryKeys("user", {
  me: null,
  getOne: (id: Users["GET_ONE"]["RequestPath"]["id"]) => [id],
  getAll: null,
});

export function getMe() {
  return get<Users["GET_ME"]["Response"]>("/api/users/me");
}

export const useMutationUpdateUser = (
  options?: UseMutationOptions<
    Users["PATCH"]["Response"],
    AxiosError,
    Users["PATCH"]["RequestPath"] & Users["PATCH"]["RequestBody"]
  >,
) => {
  return useMutation({
    mutationFn: (
      data: Users["PATCH"]["RequestPath"] & Users["PATCH"]["RequestBody"],
    ) => {
      return patch<Users["PATCH"]["Response"]>(
        `/api/users/${data.id}`,
        omit(data, ["id"]),
      );
    },
    ...options,
  });
};

export const useMutationDeleteUser = (
  options?: UseMutationOptions<
    Users["DELETE"]["Response"],
    AxiosError,
    Users["DELETE"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (params: Users["DELETE"]["RequestPath"]) => {
      return del<Users["DELETE"]["Response"]>(`/api/users/${params.id}`);
    },
    ...options,
  });
};

export const useQueryGetOneUser = (
  id: string,
  options?: Omit<
    UseQueryOptions<Users["GET_ONE"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Users["GET_ONE"]["Response"], AxiosError>({
    queryKey: usersQueryKey.getOne(id).queryKey,
    queryFn: () => {
      return get<Users["GET_ONE"]["Response"]>(`/api/users/${id}`);
    },
    ...options,
  });
};

export const useQueryGetAllUsers = (
  options?: Omit<
    UseQueryOptions<Users["GET_ALL"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Users["GET_ALL"]["Response"], AxiosError>({
    queryKey: usersQueryKey.getAll.queryKey,
    queryFn: () => {
      return get<Users["GET_ALL"]["Response"]>("/api/users");
    },
    ...options,
  });
};

export const useMutationCreateUser = (
  options?: UseMutationOptions<
    Users["POST"]["Response"],
    AxiosError,
    Users["POST"]["RequestBody"]
  >,
) => {
  return useMutation({
    mutationFn: (data: Users["POST"]["RequestBody"]) => {
      return post<Users["POST"]["Response"]>("/api/users", data);
    },
    ...options,
  });
};
